// ─────────────────────────────────────────────────────────────────────────────
// Canonical procurement lifecycle stages — single source of truth for the whole
// app. Cases, Dashboard, Opportunities, and the OpportunityDetail stepper all use
// these so a stage is labeled and colored identically everywhere.
//
// Legacy/alias stage strings that show up in older data (planning, strategy,
// execution, closed, post_award, etc.) are mapped to a canonical key by
// normalizeStage() so the UI never shows a divergent or raw label.
// ─────────────────────────────────────────────────────────────────────────────

export const WORKFLOW_STAGES = [
  'intake',
  'requirements',
  'market_research',
  'acquisition_strategy',
  'solicitation',
  'evaluation',
  'award',
  'administration_closeout',
] as const;

export type Stage = (typeof WORKFLOW_STAGES)[number];

export const STAGE_LABELS: Record<string, string> = {
  intake: 'Intake',
  requirements: 'Requirements',
  market_research: 'Market Research',
  acquisition_strategy: 'Acquisition Strategy',
  solicitation: 'Solicitation',
  evaluation: 'Evaluation',
  award: 'Award',
  administration_closeout: 'Closeout',
};

export const STAGE_ICONS: Record<string, string> = {
  intake: '📥',
  requirements: '📋',
  market_research: '🔍',
  acquisition_strategy: '📐',
  solicitation: '📢',
  evaluation: '⚖️',
  award: '🏆',
  administration_closeout: '📁',
};

// { bg, color } per stage — consistent badge palette across all screens.
export const STAGE_COLORS: Record<string, { bg: string; color: string }> = {
  intake: { bg: '#dbeafe', color: '#1d4ed8' },
  requirements: { bg: '#e0e7ff', color: '#4338ca' },
  market_research: { bg: '#cffafe', color: '#0e7490' },
  acquisition_strategy: { bg: '#ede9fe', color: '#6d28d9' },
  solicitation: { bg: '#fef9c3', color: '#854d0e' },
  evaluation: { bg: '#fce7f3', color: '#be185d' },
  award: { bg: '#d1fae5', color: '#065f46' },
  administration_closeout: { bg: '#f3f4f6', color: '#374151' },
};

// Map any legacy/alias stage string to a canonical key.
const STAGE_ALIASES: Record<string, Stage> = {
  strategy: 'acquisition_strategy',
  acquisition: 'acquisition_strategy',
  planning: 'acquisition_strategy',
  presolicitation: 'acquisition_strategy',
  market: 'market_research',
  sources_sought: 'market_research',
  rfi: 'market_research',
  solicitation_development: 'solicitation',
  rfq: 'solicitation',
  rfp: 'solicitation',
  selection: 'evaluation',
  awarded: 'award',
  execution: 'administration_closeout',
  contract_admin: 'administration_closeout',
  closeout: 'administration_closeout',
  closed: 'administration_closeout',
  complete: 'administration_closeout',
  completed: 'administration_closeout',
  post_award: 'administration_closeout',
};

export function normalizeStage(stage?: string | null): Stage {
  if (!stage) return 'intake';
  const s = String(stage).trim().toLowerCase();
  if ((WORKFLOW_STAGES as readonly string[]).includes(s)) return s as Stage;
  return STAGE_ALIASES[s] || 'intake';
}

export function stageLabel(stage?: string | null): string {
  return STAGE_LABELS[normalizeStage(stage)];
}

export function stageColor(stage?: string | null): { bg: string; color: string } {
  return STAGE_COLORS[normalizeStage(stage)];
}
